import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/hooks/AuthContext';

export interface HeaderProps {
  cartCount: number;
  onCartToggle: () => void;
}

const navLinks = [
  { to: '/catalog', label: 'Каталог' },
  { to: '/services', label: 'Пасека под ключ' },
  { to: '/delivery', label: 'Доставка' },
  { to: '/about', label: 'О нас' },
  { to: '/contacts', label: 'Контакты' },
];

export default function Header({ cartCount, onCartToggle }: HeaderProps) {
  const { user, signOut } = useAuthContext();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const mobileRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(document.body.scrollTop > 40);
    document.body.addEventListener('scroll', onScroll, { passive: true });
    return () => document.body.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const meta = user?.user_metadata || {};
  const userName = meta.first_name || meta.full_name?.split(' ')[0] || 'Пользователь';

  return (
    <header
      className={`sticky top-0 z-50 bg-card/95 backdrop-blur-md transition-shadow duration-300 [transform:translateZ(0)] ${
        scrolled ? 'shadow-card' : ''
      }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between px-4 md:px-8 lg:px-12 h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex min-w-0 items-center gap-2 group">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center md:h-11 md:w-11">
              <img
                src="/assets/readdy/khalilov-logo.png"
                alt="Крестьянское хозяйство Ивана Халилова"
                className="h-full w-full object-contain"
              />
            </div>
            <span
              className={`font-display flex max-w-[10.5rem] flex-col text-[12px] font-semibold leading-[1.05] transition-colors sm:max-w-[15rem] sm:text-sm md:max-w-[17rem] lg:max-w-[19rem] lg:text-[15px] ${
                scrolled ? 'text-dark' : 'text-dark'
              }`}
            >
              <span>Крестьянское хозяйство</span>
              <span className="whitespace-nowrap">Ивана Халилова</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  (link.to === '/services' ? location.pathname.startsWith('/services') : location.pathname === link.to)
                    ? 'bg-honey/10 text-honey-dark'
                    : 'text-dark/70 hover:text-dark hover:bg-dark/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-1 md:gap-2">
            <a
              href="https://t.me/ivankhalilov"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-dark/70 hover:text-nature hover:bg-nature/10 transition-colors whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-telegram-line text-base" />
              </div>
              <span>Telegram</span>
            </a>

            <button
              onClick={onCartToggle}
              className="relative flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-dark/70 hover:text-honey-dark hover:bg-honey/10 transition-colors whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shopping-bag-3-line text-base" />
              </div>
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-honey text-white text-[11px] font-semibold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Auth area */}
            {user ? (
              <div className="hidden lg:block relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-dark/70 hover:bg-sand/40 transition-colors whitespace-nowrap"
                >
                  <div className="w-7 h-7 rounded-full bg-honey/15 flex items-center justify-center text-honey-dark font-semibold text-xs">
                    {userName.slice(0, 2).toUpperCase()}
                  </div>
                  <span className="max-w-[80px] truncate">{userName}</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-dark/30 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {profileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-card rounded-2xl shadow-hover border border-sand/40 py-2 z-50">
                    <div className="px-4 py-3 border-b border-sand/40">
                      <p className="text-sm font-medium text-dark truncate">{userName}</p>
                      <p className="text-xs text-dark/40 truncate">{user.email}</p>
                    </div>
                    <Link
                      to="/lk"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-dark/70 hover:bg-sand/30 hover:text-dark transition-colors"
                      onClick={() => setProfileOpen(false)}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-user-line text-honey" />
                      </div>
                      Личный кабинет
                    </Link>
                    <Link
                      to="/lk-orders"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-dark/70 hover:bg-sand/30 hover:text-dark transition-colors"
                      onClick={() => setProfileOpen(false)}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-file-list-3-line text-honey" />
                      </div>
                      Мои заказы
                    </Link>
                    <div className="h-px bg-sand/40 my-1" />
                    <button
                      onClick={async () => {
                        setProfileOpen(false);
                        await signOut();
                      }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-500/70 hover:bg-red-50 hover:text-red-600 transition-colors w-full text-left"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-logout-box-r-line" />
                      </div>
                      Выйти
                    </button>
                  </div>
                )}
              </div>
            ) : null}

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark/5 transition-colors"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Меню"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-lg`} />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          ref={mobileRef}
          className="lg:hidden bg-card/95 backdrop-blur-md border-t border-sand/60 shadow-soft"
        >
          <nav className="flex flex-col p-4 gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-3 rounded-2xl text-sm font-medium transition-colors ${
                  (link.to === '/services' ? location.pathname.startsWith('/services') : location.pathname === link.to)
                    ? 'bg-honey/10 text-honey-dark'
                    : 'text-dark/70 hover:text-dark hover:bg-dark/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="h-px bg-sand/60 my-2" />
            <a
              href="https://t.me/ivankhalilov"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-medium text-dark/70 hover:text-nature hover:bg-nature/10 transition-colors"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-telegram-line text-base" />
              </div>
              Telegram
            </a>
            {user ? (
              <>
                <Link
                  to="/lk"
                  className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-medium text-dark/70 hover:text-dark hover:bg-dark/5 transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-line text-base" />
                  </div>
                  Личный кабинет
                </Link>
                <Link
                  to="/lk-orders"
                  className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-medium text-dark/70 hover:text-dark hover:bg-dark/5 transition-colors"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-base" />
                  </div>
                  Мои заказы
                </Link>
                <button
                  onClick={async () => {
                    await signOut();
                    setMobileOpen(false);
                  }}
                  className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm font-medium text-red-500/70 hover:bg-red-50 hover:text-red-600 transition-colors text-left"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-logout-box-r-line text-base" />
                  </div>
                  Выйти
                </button>
              </>
            ) : null}
          </nav>
        </div>
      )}
    </header>
  );
}
